import React, {
  useCallback,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
  useLayoutEffect,
} from 'react';
import { SignupCallback, PrivacyStatus } from '../types';
import { ForcedState, TEXT_DIRECTION } from '../../../../constants';
import { SIGNUP_FORM_ID } from '../../../../constants/forms';
import { ICaptchaSettingsOption, IServerErrorResponse } from '../../../types';
import { serverErrorsHandler } from '../../../utils/errorHandler';
import { ICaptchaImperativeActions } from '../../../Captcha/Captcha.types';
import { useBi, useEnvironment, useTranslation } from '@wix/yoshi-flow-editor';
import Captcha from '../../../Captcha/viewer/Captcha';
import { useCaptchaWithFallback } from '../../../Captcha/providers/useCaptchaWithFallback';
import {
  FormViewer,
  FormViewerContext,
  FormViewerHandle,
} from '@wix/form-viewer/widget';
import { ForcedState as FormFieldsForcedState } from '@wix/form-fields';
import { FormValues, FormError } from '@wix/form-viewer';
import { convertFormValuesToContactInfo } from '../../../utils/contactInfoConverter';
import { classes } from '../../../common/FormSection/FormSection.st.css';

const AUTHENTICATION_CANCELED = 'authentication canceled';

export interface SignupFormSectionRef {
  submit: () => Promise<void>;
}

interface SignupFormSectionProps {
  register: SignupCallback;
  setError: (error?: string) => void;
  isEditor: boolean;
  forceView?: ForcedState;
  recaptchaOption: ICaptchaSettingsOption;
  isCommunityChecked: boolean;
  setIsCommunityChecked: (checked: boolean) => void;
  props?: any;
  onLoadingChange: (isLoading: boolean) => void;
  closeLightbox?: () => Promise<void>;
  checkboxSection?: React.ReactNode;
  autoFocus?: boolean;
}

export const SignupFormSection = forwardRef<
  SignupFormSectionRef,
  SignupFormSectionProps
>(
  (
    {
      register,
      setError,
      isEditor,
      forceView,
      recaptchaOption,
      isCommunityChecked,
      props,
      onLoadingChange,
      closeLightbox,
      checkboxSection,
      autoFocus = false,
    },
    ref,
  ) => {
    const bi = useBi();
    const { i18n, t } = useTranslation();
    const formViewer = useRef<FormViewerHandle>(null);
    const captchaRef = useRef<ICaptchaImperativeActions>(null);
    const formContainerRef = useRef<HTMLDivElement>(null);

    useLayoutEffect(() => {
      if (autoFocus && !isEditor) {
        requestAnimationFrame(() => {
          const firstInput = formContainerRef.current?.querySelector('input');
          firstInput?.focus();
        });
      }
    }, [autoFocus, isEditor]);
    const [formValues, setFormValues] = useState<FormValues>({});
    const [formErrors, setFormErrors] = useState<FormError[]>([]);
    const [recaptchaToken, setReCaptchaToken] = useState('');
    const [captchaError, setCaptchaError] = useState('');
    const { isRTL } = useEnvironment();

    const {
      shouldShowVisibleCaptcha,
      tryGetInvisibleToken,
      handleCaptchaError,
    } = useCaptchaWithFallback({
      recaptchaOption,
      isEditor,
      language: i18n.language,
      action: 'signup',
    });

    const handleFormChange = useCallback(
      (values: FormValues) => {
        setFormValues(values);
        setError(); // Clear global error when user changes input
      },
      [setError],
    );

    const handleFormValidate = useCallback((errors: FormError[]) => {
      setFormErrors(errors);
    }, []);

    const handlerServerError = useCallback(
      (error: string | number | IServerErrorResponse): void => {
        // In case of admin approval / email approval flow the dialog will be replaced with
        // another dialog so the following refs will be lost.
        if (error === AUTHENTICATION_CANCELED) {
          return;
        }
        const { errorKey, errorCode } = serverErrorsHandler(error);
        handleCaptchaError(errorCode);

        if (shouldShowVisibleCaptcha) {
          captchaRef?.current?.reset?.();
          setReCaptchaToken('');
          setCaptchaError('');
        }

        const errorMessage = t(errorKey, {
          defaultValue: t('membersErrorGeneral'),
        });
        setError(errorMessage);
      },
      [shouldShowVisibleCaptcha, setError, t, handleCaptchaError],
    );

    const submit = useCallback(async () => {
      onLoadingChange(true);
      setError(); // Clear any previous errors

      try {
        const isValidated = await formViewer.current?.validate();
        if (!isValidated) {
          return;
        }

        if (shouldShowVisibleCaptcha && !recaptchaToken) {
          setCaptchaError(t('memberAuthentication.uouScreens.recaptcha.error'));
          return;
        } else {
          setCaptchaError('');
        }

        const { email, password, ...additionalData } = formValues;
        const contactInfo = convertFormValuesToContactInfo(additionalData);

        // If visible captcha is being used and we have a token, skip invisible captcha
        let invisibleRecaptchaToken: string | undefined;
        if (!shouldShowVisibleCaptcha) {
          const result = await tryGetInvisibleToken();
          if (result.status === 'VISIBLE_CAPTCHA_REQUIRED') {
            // Fallback triggered, visible captcha will appear on next render
            return;
          }
          invisibleRecaptchaToken = result.token;
        }

        if (typeof email === 'string' && typeof password === 'string') {
          await register(email, password, {
            contactInfo,
            recaptchaToken,
            invisibleRecaptchaToken,
            privacyStatus: isCommunityChecked
              ? PrivacyStatus.PUBLIC
              : PrivacyStatus.UNDEFINED,
            clientMetaData: { shouldSubmitContactDataToForms: true },
          });
        } else {
          console.error('Invalid form values:', { email, password });
        }
      } catch (error: any) {
        const _error = error?.response?.data ?? error;
        handlerServerError(_error as string | number | IServerErrorResponse);
      } finally {
        onLoadingChange(false);
      }
    }, [
      onLoadingChange,
      setError,
      shouldShowVisibleCaptcha,
      recaptchaToken,
      formValues,
      t,
      register,
      isCommunityChecked,
      handlerServerError,
      tryGetInvisibleToken,
    ]);

    // Expose submit method to parent via ref
    useImperativeHandle(
      ref,
      () => ({
        submit,
      }),
      [submit],
    );

    // Map sm-forms-app ForcedState to form-fields ForcedState
    const mapToFormFieldsForcedState = (
      state?: ForcedState,
    ): FormFieldsForcedState[] => {
      if (!state) {
        return [];
      }

      switch (state) {
        case ForcedState.INPUT_FIELDS_HOVER:
          return [FormFieldsForcedState.INPUT_FIELDS_HOVER];
        case ForcedState.INPUT_FIELDS_ERROR:
          return [FormFieldsForcedState.INPUT_FIELDS_ERROR];
        default:
          return [];
      }
    };

    return (
      <div ref={formContainerRef} className={classes.formContainer}>
        <FormViewerContext {...props}>
          <FormViewer
            onChange={handleFormChange}
            onValidate={handleFormValidate}
            values={formValues}
            errors={formErrors}
            i18n={i18n}
            formId={SIGNUP_FORM_ID}
            bi={bi}
            ref={formViewer}
            forcedState={mapToFormFieldsForcedState(forceView)}
          />
        </FormViewerContext>
        {checkboxSection}
        {shouldShowVisibleCaptcha && (
          <div data-hook="signup-recaptcha" className={classes.recaptcha}>
            <Captcha
              language={i18n.language}
              id="signup-captcha"
              token={recaptchaToken}
              onTokenChange={(token = '') => {
                setReCaptchaToken(token);
                setCaptchaError('');
              }}
              isMobileFriendly={false}
              ref={captchaRef}
            />
            {captchaError && (
              <div
                data-hook="signup-captcha-error"
                className={classes.captchaError}
                dir={isRTL ? TEXT_DIRECTION.RTL : TEXT_DIRECTION.LTR}
              >
                {captchaError}
              </div>
            )}
          </div>
        )}
      </div>
    );
  },
);
